/**
 * Módulo WhatsApp - Versão otimizada
 * 
 * Gerencia a interação com o WhatsApp de forma simples e eficiente
 * 
 * @copyright 2017-2025, Ronan Luz
 * @version 3.3.0
 */

'use strict';

/**
 * De que REGIÃO da página veio o clique: 'menu', 'rodape' ou 'pagina'.
 *
 * Existe porque whatsapp_source/tel_source dizem a SEÇÃO ('home'), não a posição:
 * um clique no rodapé e um no herói da home chegavam idênticos no GA4, e não havia
 * como saber se o rodapé converte (pedido de 2026-09-13).
 *
 * Derivado da posição no DOM, e não de atributo marcado botão a botão, para que
 * botão novo no rodapé já nasça medido sem ninguém lembrar de nada. Um
 * [data-origem] no próprio elemento ou num ancestral vence a derivação — é a
 * porta para rotular um bloco específico ('modal', 'lp-herói'…) sem mexer aqui.
 *
 * ⚠️ Seletores PRECISOS de propósito: `header` genérico casaria o
 * <header class="post-cabecalho"> dos posts de /dicas e mandaria CTA de conteúdo
 * para 'menu'. O rodapé do site é o <footer> filho direto do <body>.
 *
 * Os valores só aparecem no GA4 depois de (1) o parâmetro ser mapeado na tag do
 * evento no GTM e (2) registrado como dimensão personalizada — nenhum dos dois é
 * retroativo. Checklist em docs/rotinas/medicao-gtm-checklist.md.
 *
 * @param {Element} el - elemento clicado (o <a>)
 * @returns {string}
 */
const origemDoClique = (el) => {
    const rotulado = el.closest('[data-origem]');
    if (rotulado && rotulado.dataset.origem) return rotulado.dataset.origem;
    if (el.closest('header.sidebar-menuicon')) return 'menu';
    if (el.closest('body > footer')) return 'rodape';
    return 'pagina';
};

/**
 * Contagem de referência do clique de contato, independente do Google.
 *
 * Por que existe (2026-10-12): o `whatsapp_click` caiu de 14 para 4 no GA4 numa janela em
 * que mudaram DUAS coisas — consentimento negado por padrão (21/09: quem não aceita o
 * banner não aparece como evento) e GTM só no `load` (23/09: clique antes do load se perde
 * quando a aba vai para segundo plano). Sem uma contagem que não dependa do GTM não há como
 * separar perda técnica de limite legal. O beacon grava, por clique, se o GTM já estava
 * carregado, a decisão do banner e a fase de carga; a diferença para o GA4 se reparte aí.
 *
 * ⚠️ Sem cookie e sem identificador do visitante: só categorias fechadas (o endpoint
 * descarta o resto). `sendBeacon` sobrevive à aba ir para segundo plano — é o único
 * transporte que o navegador entrega depois que o WhatsApp abre. O POST não passa pelo
 * service worker (sw.js ignora método diferente de GET).
 *
 * ⚠️ À prova de falha: nada aqui pode impedir o WhatsApp de abrir.
 *
 * @param {string} tipo - 'whatsapp' ou 'tel'
 * @param {string} origem - região da página (origemDoClique)
 * @param {string} pagina - seção ou caminho (mesmo valor do *_source do dataLayer)
 */
const registrarCliqueContato = (tipo, origem, pagina) => {
    try {
        const gtm = !!(window.google_tag_manager && window.google_tag_manager['GTM-KQZNWJ']);
        let consent = 'pendente';
        try {
            const d = window.localStorage.getItem('privacidade');
            if (d === 'granted' || d === 'true') consent = 'granted';
            else if (d === 'denied') consent = 'denied';
        } catch (e) {
            consent = 'indisponivel';
        }
        if (navigator.sendBeacon) {
            const corpo = new URLSearchParams();
            corpo.set('tipo', tipo);
            corpo.set('origem', String(origem).slice(0, 40));
            corpo.set('pagina', String(pagina).slice(0, 120));
            corpo.set('gtm', gtm ? '1' : '0');
            corpo.set('consent', consent);
            corpo.set('carga', document.readyState);
            navigator.sendBeacon('/ajax/clique-contato.php', corpo);
        }
    } catch (e) {
        /* medição nunca derruba o lead */
    }
    // Depois do beacon, para o campo `gtm` dizer o estado NO clique.
    try {
        if (typeof window.odlCarregarGtm === 'function') window.odlCarregarGtm();
    } catch (e) { /* idem */ }
};

/**
 * Singleton do WhatsApp
 */
const whatsapp = (() => {
    // Configuração padrão
    const config = {
        prefixoBrasil: '55',
        seletorPadrao: '[data-whatsapp="true"]'  // Alterado para um seletor mais comum
    };

    /**
     * Formata um número de telefone removendo caracteres não numéricos
     * @param {string} numero - Número a ser formatado
     * @returns {string} Número formatado
     */
    const formatarNumero = (numero) => {
        if (!numero) return '';
        return numero.replace(/\D+/g, "");
    };

    /**
     * Verifica se um número de telefone é válido (10-11 dígitos)
     * @param {string} numero - Número a ser validado
     * @returns {boolean} Verdadeiro se o número for válido
     */
    const numeroValido = (numero) => {
        if (!numero) return false;
        const apenasNumeros = formatarNumero(numero);
        return apenasNumeros.length >= 10 && apenasNumeros.length <= 11;
    };

    /**
     * Obtém a URL correta do WhatsApp baseada no dispositivo
     * @param {string} numero - Número de telefone formatado
     * @param {string} mensagem - Mensagem a ser enviada
     * @returns {string} URL completa do WhatsApp
     */
    /**
     * Rastreio de clique pago — liga o anúncio ao prontuário.
     *
     * A conversão da conta de Ads mede "abriu o WhatsApp", não "virou paciente". Sem
     * ligar o clique ao desfecho não existe custo por paciente, e sem isso nenhuma
     * decisão de verba é segura. Aqui o visitante que chegou por anúncio recebe um
     * código curto que ele cita na mensagem; a recepção registra na ficha e o
     * Offline Conversion Import devolve o desfecho ao Google.
     * Ver docs/planos/2026-08-31-economia-unitaria-e-prioridades.md
     *
     * ⚠️ NÃO usa cookie. O identificador vai para log de servidor e o código fica em
     * sessionStorage — o portão do Consent Mode v2 (ad_storage negado por padrão)
     * continua intacto, porque não há armazenamento persistente no dispositivo.
     *
     * ⚠️ Tudo aqui é à prova de falha: qualquer erro deixa a mensagem original
     * passar sem alteração. O lead vale mais que a medição dele.
     */
    const CHAVE_REF = 'odl_click_ref';
    const PARAMS_CLIQUE = ['gclid', 'wbraid', 'gbraid'];
    let tentouCapturar = false;

    const lerRef = () => {
        try {
            return window.sessionStorage.getItem(CHAVE_REF) || '';
        } catch (e) {
            return '';
        }
    };

    /**
     * Marcador do visitante ORGÂNICO: `[ref: site-<classe>]`, com a classe declarada em
     * include/routes.php (chave 'origem') e exposta pelo include/footer.php em
     * `section_origem`. A recepção cola a mensagem no mesmo campo do código de anúncio e o
     * ERP grava a classe à parte — nunca vai para o Offline Conversion Import.
     * Plano: docs/superpowers/plans/2026-09-21-marcador-origem-whatsapp.md
     *
     * A classe é a da página de ENTRADA nesta aba, não a do clique: a análise compara
     * pacientes por classe com cliques do Search Console, que contam a entrada — e o código
     * de anúncio já tem a mesma semântica (click-ref.php registra a página de chegada).
     * Fica em sessionStorage pelo mesmo motivo do código pago: nada persiste no dispositivo.
     *
     * Uma marca por mensagem: com código de anúncio, sai SÓ `[ref: XXXX]`.
     */
    const CHAVE_CLASSE = 'odl_site_classe';
    const CLASSE_VALIDA = /^[a-z]{3,12}$/;

    const classeDaPagina = () => {
        const c = (typeof section_origem !== 'undefined' && section_origem) ? String(section_origem) : '';
        return CLASSE_VALIDA.test(c) ? c : '';
    };

    const lerClasse = () => {
        try {
            const c = window.sessionStorage.getItem(CHAVE_CLASSE) || '';
            return CLASSE_VALIDA.test(c) ? c : '';
        } catch (e) {
            return '';
        }
    };

    const capturarClasseDeEntrada = () => {
        try {
            if (lerClasse()) return;
            const c = classeDaPagina();
            if (c) window.sessionStorage.setItem(CHAVE_CLASSE, c);
        } catch (e) {
            /* modo privado: no clique, cai na classe da página atual */
        }
    };

    const marcaDeOrigem = () => {
        const ref = lerRef();
        if (ref) return `[ref: ${ref}]`;
        const classe = lerClasse() || classeDaPagina();
        return classe ? `[ref: site-${classe}]` : '';
    };

    const comReferencia = (mensagem) => {
        try {
            const marca = marcaDeOrigem();
            if (!marca) return mensagem;
            if (mensagem && mensagem.indexOf(marca) !== -1) return mensagem;
            return mensagem ? `${mensagem.trim()} ${marca}` : marca;
        } catch (e) {
            return mensagem;
        }
    };

    /**
     * Monta a URL do WhatsApp, já com o código de referência quando houver.
     * @param {string} numero - Número de telefone formatado
     * @param {string} mensagem - Mensagem a ser enviada
     * @returns {string} URL completa do WhatsApp
     */
    const getWhatsAppUrl = (numero, mensagem = '') => {
        const texto = comReferencia(mensagem);
        const textParam = texto ? `&text=${encodeURIComponent(texto.trim())}` : '';
        return `https:\/\/api.whatsapp.com/send?phone=${config.prefixoBrasil}${numero}${textParam}`;
    };

    /**
     * Captura o identificador de clique da URL e troca por um código curto.
     * Só dispara para visitante vindo de anúncio (~250/mês) — o resto não faz request.
     *
     * ⚠️ NÃO depende do DOM nem de `init()`. A primeira versão só rodava dentro de
     * `whatsapp.init()`, que acontece em `DOMContentLoaded` e, no fluxo SPA, depois de
     * um `await` de bundle de seção que pode levar 4 s. Resultado medido em 01/09:
     * **3 capturas para 8 cliques pagos que abriram o site (38%)** — quem chegava pelo
     * anúncio e saía em poucos segundos era perdido. Agora roda assim que o script é
     * avaliado (`defer`, portanto após o parse do HTML e antes do `DOMContentLoaded`),
     * que é o mais cedo possível sem inline no `<head>`.
     */
    const capturarCliquePago = () => {
        try {
            if (tentouCapturar) return;
            tentouCapturar = true;

            if (lerRef()) return;

            const q = new URLSearchParams(window.location.search);
            let tipo = '';
            let id = '';
            for (const p of PARAMS_CLIQUE) {
                const v = q.get(p);
                if (v && /^[A-Za-z0-9_-]{10,200}$/.test(v)) {
                    tipo = p;
                    id = v;
                    break;
                }
            }
            if (!id) return;

            const corpo = new URLSearchParams();
            corpo.set(tipo, id);
            corpo.set('pagina', window.location.pathname.slice(0, 200));
            const utm = q.get('utm_campaign');
            if (utm) corpo.set('utm', utm.slice(0, 200));

            fetch('/ajax/click-ref.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: corpo.toString(),
                credentials: 'same-origin',
                keepalive: true
            })
                .then((r) => (r.ok ? r.json() : null))
                .then((d) => {
                    if (d && typeof d.ref === 'string' && /^[0-9A-Z]{4}$/.test(d.ref)) {
                        try {
                            window.sessionStorage.setItem(CHAVE_REF, d.ref);
                        } catch (e) { /* modo privado: segue sem código */ }
                    }
                })
                .catch(() => { /* medição nunca derruba o fluxo do lead */ });
        } catch (e) {
            /* idem */
        }
    };

    /**
     * Abre uma janela do WhatsApp
     * @param {string} url - URL do WhatsApp
     * @returns {Window|undefined} Janela popup aberta ou undefined em caso de erro
     */
    const abrirJanela = (url) => {
        try {
            return window.open(
                url,
                "whatsapp_popup",
                "scrollbars=yes,resizable=yes,width=700,height=700"
            );
        } catch (error) {
            console.error('Erro ao abrir janela do WhatsApp:', error);
            return undefined;
        }
    };

    /**
     * Configura os botões de WhatsApp no documento
     * @param {string} [seletor=config.seletorPadrao] - Seletor CSS para elementos WhatsApp
     * @returns {Object} API pública do módulo
     */
    const configurarBotoes = (seletor = config.seletorPadrao) => {
        const elementos = document.querySelectorAll(seletor);

        if (!elementos.length) {
            return whatsapp; // Retorna o módulo mesmo sem elementos
        }

        // Configura evento de clique
        elementos.forEach(el => {
            // whatsapp.init() é reexecutado a cada troca de página via AJAX (pageLoader.load).
            // Botões persistentes do menu/rodapé não são recriados nessa navegação, então
            // sem essa guarda acumulariam um listener por navegação, inflando whatsapp_click.
            if (el.dataset.whatsappBound === '1') {
                return;
            }
            el.dataset.whatsappBound = '1';

            el.addEventListener('click', (e) => {
                e.preventDefault();

                const telefone = formatarNumero(el.dataset.tel || '');
                const mensagem = el.dataset.msg || '';

                if (numeroValido(telefone)) {
                    // Rastreio de conversão (GTM -> Google Ads / GA4).
                    // Crie no GTM um gatilho de Evento Personalizado "whatsapp_click"
                    // e associe a uma tag de conversão do Google Ads.
                    const origem = origemDoClique(el);
                    const pagina = (typeof section_page !== 'undefined' && section_page) ? section_page : (location.pathname || '/');
                    window.dataLayer = window.dataLayer || [];
                    window.dataLayer.push({
                        event: 'whatsapp_click',
                        whatsapp_source: pagina,
                        whatsapp_number: telefone,
                        whatsapp_origem: origem
                    });
                    registrarCliqueContato('whatsapp', origem, pagina);

                    const url = getWhatsAppUrl(telefone, mensagem);
                    abrirJanela(url);
                } else {
                    console.warn(`Número de WhatsApp inválido: ${telefone}`);
                }
            });
        });

        return whatsapp;
    };

    // API pública do módulo
    return {
        /**
         * Inicializa os botões de WhatsApp no documento
         * @param {string} [seletor] - Seletor CSS opcional para elementos WhatsApp
         * @returns {Object} API pública do módulo
         */
        init: (seletor) => configurarBotoes(seletor),

        /** Exposto só para teste; o disparo real acontece na avaliação do módulo. */
        _capturarCliquePago: capturarCliquePago,

        /** Idem: a captura real roda na avaliação do módulo, logo abaixo. */
        _capturarClasseDeEntrada: capturarClasseDeEntrada,

        /** Exposto só para teste (bin/teste-marcador-origem.js). */
        _comReferencia: comReferencia
    };
})();

// A captura do clique pago roda AQUI, na avaliação do script, e não em
// DOMContentLoaded nem dentro de init(): é o mais cedo possível para não perder
// quem chega pelo anúncio e sai em poucos segundos. Não toca no DOM.
whatsapp._capturarCliquePago();
// A classe de entrada é capturada no mesmo momento, pelo mesmo motivo: na navegação SPA
// do menu este script não é reavaliado, e é a primeira página da aba que conta.
whatsapp._capturarClasseDeEntrada();

/**
 * Rastreia cliques em links de ligação (href="tel:...") como conversão.
 * Delegado no document para cobrir links estáticos e injetados em modais.
 * Gatilho no GTM: Evento Personalizado "tel_click".
 */
const rastrearTelefones = () => {
    document.addEventListener('click', (e) => {
        const link = e.target.closest('a[href^="tel:"]');
        if (!link) return;

        const numero = (link.getAttribute('href') || '').replace(/^tel:/, '').replace(/\D+/g, '');
        if (!numero) return;

        const origem = origemDoClique(link);
        const pagina = (typeof section_page !== 'undefined' && section_page) ? section_page : (location.pathname || '/');
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: 'tel_click',
            tel_source: pagina,
            tel_number: numero,
            tel_origem: origem
        });
        registrarCliqueContato('tel', origem, pagina);
    });
};

// Inicializa o módulo quando o DOM estiver completamente carregado
document.addEventListener('DOMContentLoaded', () => {
    whatsapp.init();
    rastrearTelefones();
});